import * as React from 'react';

import { useSearchParams } from 'react-router-dom';

import { useGoposVenues } from '@/api/gopos';
import type { AirtableSelectOption } from '@/components/ui/airtable-select';

import {
  buildGoposOrdersTopbarDateRangeParams,
  buildGoposOrdersTopbarFilterParams,
  buildResetGoposOrdersTopbarParams,
  parseGoposOrdersTopbarFilters,
} from './goposOrdersTopbarState';
import { useGoposOrdersSearchController } from './useTopbarSearchControllers';

export function useGoposOrdersTopbarState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { venues, isLoading: isLoadingVenues } = useGoposVenues({
    includeVirtualPosLocations: true,
  });
  const venueOptions = React.useMemo<AirtableSelectOption[]>(
    () =>
      venues.map((venue) => ({
        value: venue.id,
        label: venue.is_virtual
          ? `${venue.location_name || venue.venue_name || `Venue ${venue.venue_id}`} · POS`
          : venue.venue_name || venue.location_name || `Venue ${venue.venue_id}`,
      })),
    [venues],
  );

  const filters = React.useMemo(
    () => parseGoposOrdersTopbarFilters(searchParams, true),
    [searchParams],
  );

  const { searchInput, handleSearchChange } = useGoposOrdersSearchController({
    orderNumber: filters.order_number ?? '',
    searchParams,
    setSearchParams,
  });

  const handleDateRangeChange = React.useCallback(
    (from: string | undefined, to: string | undefined) => {
      setSearchParams(buildGoposOrdersTopbarDateRangeParams(searchParams, from, to), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );

  const handleVenueChange = React.useCallback(
    (value: string | null) => {
      setSearchParams(buildGoposOrdersTopbarFilterParams(searchParams, 'venue_mapping_id', value), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );

  const handleResetAllFilters = React.useCallback(() => {
    setSearchParams(buildResetGoposOrdersTopbarParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    filters,
    venueOptions,
    isLoadingVenues,
    searchInput,
    handleSearchChange,
    handleDateRangeChange,
    handleVenueChange,
    handleResetAllFilters,
  };
}
